/* eslint-disable default-case */
import React, { useEffect, useState, useRef } from 'react';
import {
   Input, Table, Typography, Space, Tooltip, Button, List, Row, Col,
   Modal, Spin, Popconfirm, InputNumber, Tag, Tabs, Progress
} from 'antd';
import * as AntDIcons from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import SimpleBarReact from "simplebar-react";
import axios from 'axios';
import uuid from 'react-uuid';
import Utils from '../../../misc/Utils';
import Iframe from 'react-iframe';
import '../../../pages/HomeworksPage/Components/Submissions.css'
import DocumentViewer from '../../../components/document-viewer/DocumentViewer';
import { useDownloadExcel } from 'react-export-table-to-excel';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import {
   addStudentTaskEvaluation,
   listStudentTaskSubmissions,
   removeStudentTaskEvaluation,
} from '../../../services/MicrocredentialsStudentTaskService';

const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const plagiarismSupportedFileTypes = [
   "pdf", "doc", "docx", "odt", "rtf", "txt", "md", "html", "xml", "json", "csv"
];

const LOCAL_SUBDOMAIN = localStorage.getItem('lms-subdomain');
const STUDENT_UPLOADS_PATH = process.env.REACT_APP_S3_PERFORMANCE_TASK_STUDENTS_PATH || '';

function normalizeSubmissionStoredPath(storedPath) {
   const raw = String(storedPath || '').trim();

   if (!raw) return '';
   if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) return raw;
   if (raw.startsWith('/')) return raw;
   if (raw.includes('/') || raw.includes('\\')) return raw;
   if (!STUDENT_UPLOADS_PATH) return raw;

   return `${STUDENT_UPLOADS_PATH}/${raw}`;
}

function resolveStoredDocumentUrl(storedPath) {
   const raw = normalizeSubmissionStoredPath(storedPath);

   if (!raw) return null;
   if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) return raw;
   if (raw.startsWith('/') ) return `${process.env.REACT_APP_API_S3_URL}${raw}`;
   if (LOCAL_SUBDOMAIN && raw.startsWith(`${LOCAL_SUBDOMAIN}/`)) {
      return `${process.env.REACT_APP_API_S3_URL}/${raw}`;
   }

   return `${process.env.REACT_APP_API_S3_URL}/${LOCAL_SUBDOMAIN}/${raw}`;
}

function normalizeSubmissionDocuments(documentValue) {
   if (!documentValue) {
      return [];
   }

   if (Array.isArray(documentValue)) {
      return documentValue;
   }

   if (typeof documentValue === 'string') {
      const trimmed = documentValue.trim();

      if (!trimmed) {
         return [];
      }

      try {
         const parsed = JSON.parse(trimmed);
         if (Array.isArray(parsed)) {
            return parsed;
         }

         if (parsed) {
            return [parsed];
         }
      } catch (error) {
         return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
      }
   }

   return [documentValue];
}

function parseSubmissionDocuments(documentValue) {
   return normalizeSubmissionDocuments(documentValue)
      .map((item, index) => {
         if (item && typeof item === 'object' && item.s3_path) {
            const normalizedPath = normalizeSubmissionStoredPath(item.s3_path);
            const fileName = item.original_filename || normalizedPath.split(/[\\/]/).pop() || `Attachment ${index + 1}`;

            return {
               key: `document-${index}`,
               name: fileName,
               path: normalizedPath,
               url: resolveStoredDocumentUrl(normalizedPath),
               documentInfo: {
                  original_filename: fileName,
                  extension_name: item.extension_name || '',
                  s3_path: normalizedPath,
               },
            };
         }

         const storedPath = normalizeSubmissionStoredPath(item);
         if (!storedPath) {
            return null;
         }

         return {
            key: `document-${index}`,
            name: storedPath.split(/[\\/]/).pop() || storedPath,
            path: storedPath,
            url: resolveStoredDocumentUrl(storedPath),
            documentInfo: {
               original_filename: storedPath.split(/[\\/]/).pop() || storedPath,
               extension_name: `.${(storedPath.split('.').pop() || '').toLowerCase()}`.replace(/\.$/, ''),
               s3_path: storedPath,
            },
         };
      })
      .filter(Boolean);
}

function getDocumentPath(document) {
   if (!document) {
      return '';
   }

   if (typeof document === 'string') {
      return document;
   }

   return document.path || document.documentInfo?.s3_path || document.s3_path || document.url || '';
}

function getDocumentName(document) {
   if (!document) {
      return '';
   }

   if (typeof document === 'string') {
      return document.split(/[\\/]/).pop() || document;
   }

   return document.name || document.documentInfo?.original_filename || getDocumentPath(document).split(/[\\/]/).pop() || '';
}
function normalizeSubmissionListResponse(payload) {
   if (Array.isArray(payload)) {
      return payload;
   }

   if (!payload || typeof payload !== 'object') {
      return [];
   }

   const candidateLists = [
      payload.data,
      payload.rows,
      payload.items,
      payload.results,
      payload.students,
      payload.submissions,
      payload.list,
   ];

   const matchedList = candidateLists.find((candidate) => Array.isArray(candidate));
   if (matchedList) {
      return matchedList;
   }

   const objectValues = Object.values(payload);
   if (objectValues.length > 0 && objectValues.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      return objectValues;
   }

   return [];
}

export default function PerformanceTaskSubmissions(props) {
   const taskMode = props.mode || detectMicrocredentialsTaskMode(window.location?.pathname);
   const moduleConfig = getMicrocredentialsTaskModule(taskMode);
   const workTypeLabel = moduleConfig.label;
   const submissionType = 'microcredential';
   const plagiarismCheckerEnabled = props.plagiarismCheckerEnabled === true;
   const tableRef = useRef(null);

   const [searchText, setSearchText] = React.useState("");
   const [searchedColumn, setSearchedColumn] = React.useState("");

   const [submissionList, setSubmissionList] = useState([]);
   const [submissionListLoading, setSubmissionListLoading] = useState(false);
   const [checkEvaluateModalVisible, setCheckEvaluateModalVisible] = useState(false);
   const [studentName, setStudentName] = useState("");
   const [submittedDocument, setSubmittedDocument] = useState(null);
   const [documentLink, setDocumentLink] = useState(null);
   const [uploadedDocs, setUploadedDocs] = useState(null);
   const [selectedDocumentIdx, setSelectedDocumentIdx] = useState(-1);
   const [maxScore, setMaxScore] = useState(null);
   const [submissionID, setSubmissionID] = useState(null);
   const [documentTab, setDocumentTab] = useState("preview");
   const [plagiarismCheckerStatus, setPlagiarismCheckerStatus] = useState({
      url: "",
      checking: false,
      checked: false,
      exists: false,
   });

   const [evaluationData, setEvaluationData] = useState({
      Id: "",
      Homework_submitted_id: "",
      Score: null,
      Remarks: ""
   })
   const [score, setScore] = useState(0);
   const [remarks, setRemarks] = useState("");
   const [evaluating, setEvaluating] = useState(false);

   useEffect(() => {
      Utils.verifyJwt();


      getStudentSubmissions(props.homeworkID, props.levelID, props.assignmentID, props.assignedBy);
   }, [props.assignedBy, props.assignmentID, props.homeworkID, props.levelID]);

   const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   };

   const handleReset = clearFilters => {
      clearFilters();
      setSearchText("");
   };

   const getColumnSearchProps = dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               ref={node => {
                  //this.searchInput(node);
               }}
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<AntDIcons.SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}>
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  // onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
               {/* <Button
                  type="link"
                  size="small"
                  onClick={() => {
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Filter
               </Button> */}
            </Space>
         </div>
      ),
      filterIcon: filtered => <AntDIcons.SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      // onFilterDropdownVisibleChange: visible => {
      //    if (visible) {
      //       setTimeout(() => this.searchInput.select(), 100);
      //    }
      // },
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   });

   const SubmissionListColumns = [
      {
         title: 'Student',
         key: 'student_name',
         dataIndex: 'student_name',
         sorter: (a, b) => { return (a.student_name === null ? '' : a.student_name).localeCompare(b.student_name === null ? '' : b.student_name) },
         ...getColumnSearchProps('student_name'),
         ellipsis: {
            showTitle: true,
         },
         render: student_name => (
            <Tooltip placement="right" title={student_name}>
               <span style={{ cursor: "default" }}>{student_name}</span>
            </Tooltip>
         ),
         width: '14%'
      },
      {
         title: 'Section',
         key: 'section_name',
         dataIndex: 'section_name',
         sorter: (a, b) => { return (a.section_name === null ? '' : a.section_name).localeCompare(b.section_name === null ? '' : b.section_name) },
         ...getColumnSearchProps('section_name'),
         ellipsis: {
            showTitle: true,
         },
         render: section_name => (
            <Tooltip placement="right" title={section_name}>
               <span style={{ cursor: "default" }}>{section_name}</span>
            </Tooltip>
         ),
         width: '12%'
      },
      {
         title: 'Student Message',
         key: 'message',
         dataIndex: 'message',
         // sorter: (a, b) => { return (a.message === null ? '' : a.message).localeCompare(b.message === null ? '' : b.message) },
         ellipsis: {
            showTitle: true,
         },
         render: message => (
            <Tooltip placement="right" title={message}>
               <span style={{ cursor: "default" }}>{message}</span>
            </Tooltip>
         ),
         width: '13%'
      },
      {
         title: 'Document Link',
         key: 'url_link',
         hidden: true,
         // dataIndex:'url_link',
         // width:'10%'
      },
      {
         title: 'Date Submitted',
         key: 'created_at',
         sorter: (a, b) => { return (a.created_at === null ? '' : a.created_at).localeCompare(b.created_at === null ? '' : b.created_at) },
         render: payload => {
            return <>
               {payload.is_late && <Tag color="red" style={{ marginLeft: "5px" }}>Late</Tag>}
               {payload.created_at}
            </>
         },
         width: '14%'
      },
      {
         title: 'Score',
         key: 'score',
         sorter: (a, b) => a.score - b.score,
         render: payload => {
            return <>
               {
                  payload.max_score !== null
                     ?
                     <span>{payload.score === 0 ? '' : payload.score + "/" + payload.max_score}</span>
                     :
                     <span>{payload.score === 0 ? '' : payload.score}</span>
               }
            </>
         },
         width: '6%'
      },
      {
         title: 'Remarks',
         key: 'remarks',
         dataIndex: 'remarks',
         ellipsis: {
            showTitle: true,
         },
         render: remarks => (
            <Tooltip placement="left" title={remarks}>
               <span style={{ cursor: "default" }}>{remarks}</span>
            </Tooltip>
         ),
         width: '14%'
      },
      {
         title: 'Evaluation Date',
         key: 'evaluation_date',
         dataIndex: 'evaluation_date',
         width: '11%'
      },
      {
         title: 'Action',
         key: 'action',
         render: payload => {
            return <>
               {payload.created_at
                  ?
                  <Space size="small">
                     {// payload.evaluation_id === null &&
                        <Tooltip placement="top" title='Evaluate'>
                           <AntDIcons.FormOutlined color='#4abec7' style={{ cursor: 'pointer' }}
                              onClick={() => handleEvaluate(payload.id, payload.student_name, payload.document, payload.url_link, payload.evaluation_id, payload.score, payload.remarks, payload.max_score)}
                           />
                        </Tooltip>
                     }
                     {payload.evaluation_id !== null &&
                        <Tooltip placement="top" title='Remove Evaluation'>
                           <Popconfirm
                              title="Are you sure you want to remove the evaluation?"
                              onConfirm={() => handleRemoveEvaluation(payload.evaluation_id)}
                              okText="Yes"
                              cancelText="No"
                           >
                              <AntDIcons.DeleteOutlined color='#4abec7' style={{ cursor: 'pointer' }} />
                           </Popconfirm>
                        </Tooltip>
                     }
                  </Space>
                  :
                  <></>
               }
            </>
         },
         width: '5%'
      },
   ].filter(item => !item.hidden);

   const getStudentSubmissions = (homework_id, level, assignmentId, assignedBy) => {
      setSubmissionListLoading(true);

      listStudentTaskSubmissions({
            id: homework_id,
            performance_task_id: homework_id,
            task_id: homework_id,
            level,
            type: submissionType,
            assignment_id: assignmentId,
            mcpta_id: assignmentId,
            performance_task_assignment_id: assignmentId,
            assigned_by: assignedBy,
         }, taskMode).then((response) => {
         const data = normalizeSubmissionListResponse(response?.data ?? response);
         setSubmissionList(data);
         setSubmissionListLoading(false);
      }, (error) => {
         setSubmissionListLoading(false);
         console.log(error.message);
      });
   }

   const handleEvaluate = (id, student_name, document, document_link, e_id, score, remarks, max_score) => {
      setDocumentTab("preview");
      setPlagiarismCheckerStatus({
         url: "",
         checking: false,
         checked: false,
         exists: false,
      });

      const docs = parseSubmissionDocuments(document);

      if (docs.length > 0) {
         setUploadedDocs(docs);
         setSelectedDocumentIdx(0);
         setSubmittedDocument(docs[0]);
      } else {
         setUploadedDocs(null);
         setSelectedDocumentIdx(-1);
         setSubmittedDocument(null);
      }

      setDocumentLink(document_link);
      setStudentName(student_name);
      setScore(score || 0);
      setRemarks(remarks || "");
      setMaxScore(max_score);
      setSubmissionID(id);

      var o2o_mode = Utils.determineOnlineOffline(); //window.location.hostname.match(/localhost|[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}|::1|\.local|^$/gi) ? "offline" : "online";
      var evaluation_id = "performance_task_evaluation_" + o2o_mode + "_" + uuid();

      var initialEvaluationData = {
         Id: e_id === '' || e_id === null ? evaluation_id : e_id,
         Homework_submitted_id: id,
         Score: null,
         Remarks: null
      }

      // setScore(null);
      // setRemarks(null);

      setEvaluationData(initialEvaluationData);
      setCheckEvaluateModalVisible(true);
   }

   const handleSaveEvaluation = () => {
      if (score !== 0 || remarks !== '') {
         setEvaluating(true);

         var finalEvaluationData = {
            Id: evaluationData.Id,
            Homework_submitted_id: evaluationData.Homework_submitted_id,
            PerformanceTask_submitted_id: evaluationData.Homework_submitted_id,
            performance_task_submitted_id: evaluationData.Homework_submitted_id,
            Score: score,
            Remarks: remarks,
         }

         addStudentTaskEvaluation(finalEvaluationData, taskMode).then(response => {
            if ((response?.data ?? response)) {
               setEvaluating(false);

               Modal.success({
                  title: workTypeLabel,
                  content: `${workTypeLabel} evaluation added.`,
                  centered: true,
                  onOk: () => { setCheckEvaluateModalVisible(false); },
               });

               getStudentSubmissions(props.homeworkID, props.levelID, props.assignmentID, props.assignedBy);
            }
         }, (error) => {
            setEvaluating(false);
            Modal.error({
               title: workTypeLabel,
               content: error.response.data,
               centered: true,
            });
         });
      }
   }

   const handleRemoveEvaluation = (id) => {
      setEvaluating(true);

      removeStudentTaskEvaluation(id, taskMode).then(response => {
         if ((response?.data ?? response)) {
            setEvaluating(false);

            Modal.success({
               title: 'Homework',
               content: `${workTypeLabel} evaluation removed.`,
               centered: true,
            });

            getStudentSubmissions(props.homeworkID, props.levelID, props.assignmentID, props.assignedBy);
         }
      }, (error) => {
         setEvaluating(false);
         Modal.error({
            title: workTypeLabel,
            content: `Enter the reason for return in the ${workTypeLabel.toLowerCase()} remarks section.`,
            centered: true,
         });
      });
   }

   const handleCancelEvaluation = () => {
      setCheckEvaluateModalVisible(false);
   }

   const getFileExtension = (document) => {
      const documentPath = getDocumentPath(document);

      if (documentPath === '')
         return '';

      var re = /(?:\.([^.]+))?$/;
      var ext = re.exec(documentPath.toLowerCase());

      return ext && ext[1] ? ext[1] : '';
   }

   const shouldShowPlagiarismChecker = (document) => {
      return plagiarismSupportedFileTypes.includes(getFileExtension(document));
   }

   const getPlagiarismCheckerUrl = (document) => {
      const plagiarismCheckerBaseUrl = process.env.REACT_APP_PLAGIARISM_CHECKER_URL;

      if (plagiarismCheckerBaseUrl === undefined || plagiarismCheckerBaseUrl === null || plagiarismCheckerBaseUrl === '')
         return '';

      const documentPath = getDocumentPath(document);

      if (documentPath === '')
         return '';

      const fileName = documentPath.split(/[\\/]/).pop();
      const htmlFileName = fileName.replace(/\.[^/.]+$/, '') + '.html';
      const normalizedBaseUrl = plagiarismCheckerBaseUrl.endsWith('/') ? plagiarismCheckerBaseUrl : plagiarismCheckerBaseUrl + '/';

      return normalizedBaseUrl + encodeURIComponent(htmlFileName);
      // return normalizedBaseUrl + 'testfile1.html';
   }

   const getPlagiarismCheckerKey = (document) => {
      const plagiarismCheckerUrl = getPlagiarismCheckerUrl(document);

      if (plagiarismCheckerUrl === '')
         return '';

      try {
         return decodeURIComponent(new URL(plagiarismCheckerUrl).pathname.replace(/^\/+/, ''));
      } catch (error) {
         return '';
      }
   }

   const checkPlagiarismCheckerUrl = () => {
      const plagiarismCheckerUrl = getPlagiarismCheckerUrl(submittedDocument);
      const plagiarismCheckerKey = getPlagiarismCheckerKey(submittedDocument);

      if (plagiarismCheckerUrl === '' || plagiarismCheckerKey === '' || process.env.REACT_APP_API_S3_UPLOAD === undefined) {
         setPlagiarismCheckerStatus({
            url: plagiarismCheckerUrl,
            checking: false,
            checked: true,
            exists: false,
         });
         return;
      }

      setPlagiarismCheckerStatus({
         url: plagiarismCheckerUrl,
         checking: true,
         checked: false,
         exists: false,
      });

      axios.get(`${process.env.REACT_APP_API_S3_UPLOAD}/fileexists`, {
         params: {
            key: plagiarismCheckerKey,
         },
      }).then(response => {
         setPlagiarismCheckerStatus({
            url: plagiarismCheckerUrl,
            checking: false,
            checked: true,
            exists: response.data && response.data.exists === true,
         });
      }, error => {
         console.log(error.message);

         setPlagiarismCheckerStatus({
            url: plagiarismCheckerUrl,
            checking: false,
            checked: true,
            exists: false,
         });
      });
   }

   const renderPlagiarismChecker = () => {
      if (documentTab !== "plagiarism") return null;

      const plagiarismCheckerUrl = getPlagiarismCheckerUrl(submittedDocument);

      if (plagiarismCheckerUrl === undefined || plagiarismCheckerUrl === null || plagiarismCheckerUrl === '')
         return <div className='preview'>No plagiarism checker URL configured.</div>

      // if (plagiarismCheckerStatus.url !== plagiarismCheckerUrl || plagiarismCheckerStatus.checking)
      //    return <div className='preview'><Spin tip="Checking plagiarism report..." /></div>

      if (plagiarismCheckerStatus.checked && !plagiarismCheckerStatus.exists)
         return <div className='preview'>Your document is currently being scanned for potential plagiarism. Please wait a moment.</div>

      return <>
         <Iframe
            id="plagiarism-checker-iframe"
            title="plagiarism-checker-iframe"
            src={plagiarismCheckerUrl}
            frameBorder="0"
            height='100%'
            width='100%'
         />
      </>
   }

   const handleViewDoc = (doc, idx) => {
      setDocumentTab("preview");
      setPlagiarismCheckerStatus({
         url: "",
         checking: false,
         checked: false,
         exists: false,
      });
      setSelectedDocumentIdx(idx);
      setSubmittedDocument(doc);
   }

   const handleDocumentTabChange = (key) => {
      setDocumentTab(key);

      // if (key === "plagiarism")
      //    checkPlagiarismCheckerUrl();
   }

   const handleReturn = () => {
      if (remarks !== '' && remarks !== null) {
         setEvaluating(true);

         var finalEvaluationData = {
            Id: evaluationData.Id,
            Homework_submitted_id: evaluationData.Homework_submitted_id,
            PerformanceTask_submitted_id: evaluationData.Homework_submitted_id,
            performance_task_submitted_id: evaluationData.Homework_submitted_id,
            Score: score,
            Remarks: remarks,
            Returned: true
         }

         addStudentTaskEvaluation(finalEvaluationData, taskMode).then(response => {
            if ((response?.data ?? response)) {
               setEvaluating(false);

               Modal.success({
                  title: workTypeLabel,
                  content: `${workTypeLabel} return successful.`,
                  centered: true,
                  onOk: () => { setCheckEvaluateModalVisible(false); },
               });

               getStudentSubmissions(props.homeworkID, props.levelID, props.assignmentID, props.assignedBy);
            }
         }, (error) => {
            setEvaluating(false);
            Modal.error({
               title: workTypeLabel,
               content: error.response.data,
               centered: true,
            });
         });
      } else {
         Modal.info({
            title: workTypeLabel,
            content: `Enter the reason for return in the ${workTypeLabel.toLowerCase()} remarks section.`,
            centered: true,
         });
      }
   }

   const { onDownload } = useDownloadExcel({
      currentTableRef: tableRef.current,
      filename: `${workTypeLabel.toLowerCase().replace(/\s+/g, '_')}_submissions`,
      sheet: `${workTypeLabel} Submissions`
   });

   const normalizedSubmissionList = Array.isArray(submissionList) ? submissionList : [];
   const submittedCount = normalizedSubmissionList.filter(item => item.created_at).length;
   const evaluatedCount = normalizedSubmissionList.filter(item => item.evaluation_id !== null).length;
   const lateCount = normalizedSubmissionList.filter(item => item.is_late).length;
   const normalizedScore = Number(score || 0);
   const normalizedMaxScore = Number(maxScore || 0);
   const scorePercent = normalizedMaxScore > 0 ? Math.min(100, Math.round((normalizedScore / normalizedMaxScore) * 100)) : 0;

   return (
      <>
         {/* <Row gutter={12} style={{ minWidth: "400px", overflow: "auto" }}>
            <Col span={24}>
               <div className="dashboard-item-inner3">
                  <Table
                     rowKey={'uid'}
                     loading={submissionListLoading}
                     size='small'
                     columns={SubmissionListColumns}
                     dataSource={normalizedSubmissionList}
                     pagination={false}
                     scroll={{
                        y: 'calc(100svh - 20rem)',
                        x: '1000px',
                     }}
                     footer={() => 'Total Records: ' + normalizedSubmissionList.length}
                  />
               </div>
            </Col>
         </Row> */}
         <div className="homework-submissions-shell">
            <div className="homework-submissions-header">
               <div className="homework-submissions-heading">
                  <span className="homework-modal-section-icon"><AntDIcons.SolutionOutlined /></span>
                  <div>
                     <span className="homework-modal-kicker">Submissions</span>
                     <h3>{props.homeworkTitle}</h3>
                  </div>
               </div>
               <div className="homework-submissions-stats">
                  <span><AntDIcons.UserOutlined /> {normalizedSubmissionList.length} students</span>
                  <span><AntDIcons.CheckCircleOutlined /> {submittedCount} submitted</span>
                  <span><AntDIcons.FormOutlined /> {evaluatedCount} evaluated</span>
                  <span><AntDIcons.ClockCircleOutlined /> {lateCount} late</span>
               </div>
            </div>

            <div className="homework-submissions-table-shell">
               <Table
                  ref={tableRef}
                  className="homework-submissions-table"
                  rowKey={'uid'}
                  loading={submissionListLoading}
                  size='small'
                  columns={SubmissionListColumns}
                  dataSource={normalizedSubmissionList}
                  pagination={false}
                  scroll={{
                     y: 'calc(100svh - 26rem)',
                     x: '1000px',
                  }}
                  footer={() =>
                     <div className="homework-submissions-footer">
                        <span>Total Records: {normalizedSubmissionList.length}</span>
                        <Button type="primary" shape='round' size='small' icon={<AntDIcons.DownloadOutlined />} onClick={onDownload}>Export to Excel</Button>
                     </div>
                  }
               />
            </div>
         </div>

         <Modal
            title={`Evaluate ${workTypeLabel}`}
            // className="homework-assignment-modal homework-evaluation-modal"
            centered
            destroyOnClose={true}
            open={checkEvaluateModalVisible}
            okText={"Save"}
            // onOk={() => handleSaveEvaluation()}
            onCancel={() => handleCancelEvaluation()}
            // width={window.innerWidth - 400}
            width='90%'
            cancelButtonProps={{ shape: "round" }}
            okButtonProps={{ shape: "round", style: { display: 'none' }}}
         >
            <Spin spinning={evaluating} tip="Adding evaluation, please wait...">
               <div className="homework-evaluation-summary">
                  <div className="homework-evaluation-summary-title">
                     <span className="homework-modal-section-icon"><AntDIcons.AuditOutlined /></span>
                     <div>
                        <span className="homework-modal-kicker">{workTypeLabel} Evaluation</span>
                        <h3>{props.homeworkTitle}</h3>
                     </div>
                  </div>
                  <div className="homework-evaluation-summary-meta">
                     <span><AntDIcons.UserOutlined /> {studentName || "Student"}</span>
                     <span><AntDIcons.StarOutlined /> {normalizedScore}/{normalizedMaxScore || maxScore || 0}</span>
                     <span><AntDIcons.PaperClipOutlined /> {uploadedDocs !== null ? uploadedDocs.length : 0} files</span>
                  </div>
               </div>
               <Row gutter={16} className="homework-evaluation-layout">
                  <Col xs={24} md={16} lg={16}>
                     <div className="homework-evaluation-preview-panel">
                        <div className="homework-modal-section-header">
                           <span className="homework-modal-section-icon"><AntDIcons.FileSearchOutlined /></span>
                           <div>
                              <span className="homework-modal-kicker">Review</span>
                              <h3>Submission Preview</h3>
                           </div>
                        </div>
                        <Tabs
                           className="homework-evaluation-tabs"
                           activeKey={plagiarismCheckerEnabled && shouldShowPlagiarismChecker(submittedDocument) ? documentTab : "preview"}
                           onChange={(key) => handleDocumentTabChange(key)}
                           onTabClick={(key) => {
                              if (key === "plagiarism" && documentTab === "plagiarism")
                                 checkPlagiarismCheckerUrl();
                           }}
                        >
                           <TabPane tab="Preview" key="preview">
                              <div className='modal-document-viewer homework-evaluation-document-viewer'>
                                 <DocumentViewer
                                    document={submittedDocument?.url || null}
                                    documentname={getDocumentName(submittedDocument)}
                                    document_link={documentLink}
                                    dvheight="100%"
                                    emptydescription=""
                                    ismodal={true}
                                 />
                              </div>
                           </TabPane>
                           {
                              plagiarismCheckerEnabled && shouldShowPlagiarismChecker(submittedDocument) &&
                              <TabPane tab="Plagiarism Checker" key="plagiarism">
                                 <div className='modal-document-viewer homework-evaluation-document-viewer'>
                                    {renderPlagiarismChecker()}
                                 </div>
                              </TabPane>
                           }
                        </Tabs>
                     </div>
                  </Col>
                  <Col xs={24} md={8} lg={8}>
                     <SimpleBarReact className="homework-evaluation-side-scroll">
                        <div className="homework-evaluation-panel">
                           <div className="homework-modal-section-header">
                              <span className="homework-modal-section-icon"><AntDIcons.EditOutlined /></span>
                              <div>
                                 <span className="homework-modal-kicker">Evaluate</span>
                                 <h3><Text ellipsis>{studentName}</Text></h3>
                              </div>
                           </div>
                           <div className="homework-evaluation-score-card">
                              <div>
                                 <span>Current Score</span>
                                 <strong>{normalizedScore}/{normalizedMaxScore || maxScore || 0}</strong>
                              </div>
                              <Progress percent={scorePercent} size="small" showInfo={false} />
                           </div>
                           <Space direction='vertical' style={{ width: "100%" }}>
                              <label className="homework-modal-field-label">{`Score (Max: ${maxScore})`}</label>
                              <InputNumber
                                 min={0}
                                 max={maxScore}
                                 style={{ width: "100%" }}
                                 onChange={(val) => setScore(val)}
                                 value={score}
                                 placeholder="Enter score"
                              />
                              <label className="homework-modal-field-label">Remarks</label>
                              <TextArea
                                 allowClear
                                 rows={5}
                                 onChange={(event) => setRemarks(event.target.value)}
                                 placeholder="Write evaluation notes or return reason"
                                 value={remarks}
                              />
                              <Space className="homework-evaluation-actions" size={10}>
                                 <Popconfirm
                                    title="Are you sure you want to return submission?"
                                    onConfirm={() => handleReturn(submissionID)}
                                    okText="Yes"
                                    cancelText="No"
                                 >
                                    <Button shape='round' block>Return Submission</Button>
                                 </Popconfirm>
                                 <Button type='primary' shape='round' block onClick={() => handleSaveEvaluation()}>Save</Button>
                              </Space>
                           </Space>
                        </div>

                        <div className="homework-evaluation-panel">
                           <div className="homework-modal-section-header">
                              <span className="homework-modal-section-icon"><AntDIcons.PaperClipOutlined /></span>
                              <div>
                                 <span className="homework-modal-kicker">Files</span>
                                 <h3>Uploaded Documents</h3>
                              </div>
                              <span className="homework-modal-count">{uploadedDocs !== null ? uploadedDocs.length : 0}</span>
                           </div>
                           <SimpleBarReact className="homework-evaluation-documents">
                           {
                              uploadedDocs !== null &&
                              <>
                                 {
                                    <List
                                       size="small"
                                       dataSource={uploadedDocs}

                                       renderItem={(item, idx) =>
                                          <Tooltip placement="right" title={getDocumentName(item)}>
                                             <List.Item
                                                onClick={() => handleViewDoc(item, idx)}
                                                className={idx === selectedDocumentIdx ? 'document-file-selected' : 'document-file'}
                                             >
                                                {Utils.GetFileIcon(getDocumentPath(item) || getDocumentName(item))} &nbsp;
                                                {getDocumentName(item)}
                                             </List.Item>
                                          </Tooltip>
                                       }
                                    />
                                 }
                              </>
                           }
                           </SimpleBarReact>
                        </div>
                     </SimpleBarReact>
                  </Col>
               </Row>
            </Spin>
         </Modal>
      </>
   );
}
